import React from 'react';

import { AutocompleteState, createAutocomplete } from '@algolia/autocomplete-core';
import { getAlgoliaResults } from '@algolia/autocomplete-preset-algolia';
import { Hit } from '@algolia/client-search';
/** https://github.com/algolia/algoliasearch-client-javascript/issues/1381 */
import algoliasearch from 'algoliasearch/dist/algoliasearch-lite.esm.browser';
import { useIntl } from 'react-intl';

import type { ContentHit } from '../auto-complete-search-bar';
import type { SearchResponse } from '@algolia/client-search';

type ItemUrlProps = {
	item: ContentHit;
};

type QueryParamsVariantProps = {
	hitsPerPage: number;
	facetFilters?: string[];
};

const useAlogoliaAutoComplete = ({
	searchIndex,
	ALGOLIA_FACET_KEY,
	ALGOLIA_APP_ID,
	ALGOLIA_SEARCH_API_KEY,
	queryParams,
	aiEnabled = false,
}: {
	searchIndex: string;
	ALGOLIA_FACET_KEY: string;
	ALGOLIA_APP_ID: string;
	ALGOLIA_SEARCH_API_KEY: string;
	queryParams: QueryParamsVariantProps;
	aiEnabled?: boolean;
}) => {
	const intl = useIntl();
	const algoliaSearchClient = algoliasearch(ALGOLIA_APP_ID, ALGOLIA_SEARCH_API_KEY);

	const [autocompleteState, setAutocompleteState] = React.useState<AutocompleteState<ContentHit>>({
		collections: [],
		completion: null,
		context: {},
		isOpen: false,
		query: '',
		activeItemId: null,
		status: 'idle',
	});

	let placeholder = intl.formatMessage({
		id: 'globalnav.v2.search-bar.placeholder',
		defaultMessage: 'Search Keywords',
	});

	if (aiEnabled) {
		placeholder = intl.formatMessage({
			id: 'globalnav.v2.search-bar.ai-enabled.placeholder',
			defaultMessage: 'Search or ask anything like “How much does Jira cost?”',
		});
	}

	const autocomplete = React.useMemo(
		() =>
			createAutocomplete<
				ContentHit,
				React.BaseSyntheticEvent,
				React.MouseEvent,
				React.KeyboardEvent
			>({
				// eslint-disable-next-line  @typescript-eslint/no-explicit-any
				onStateChange({ state }: { state: AutocompleteState<any> }) {
					setAutocompleteState(state);
				},
				insights: true,
				getSources() {
					// eslint-disable-next-line @typescript-eslint/no-explicit-any
					const sources: any[] = [];

					// Add AI source if enabled
					if (aiEnabled) {
						sources.push({
							sourceId: 'ai-assistant',
							getItems({ query }: { query: string }) {
								// Only show AI result if there's a query
								if (!query?.trim()) {
									return [];
								}

								return [
									{
										objectID: 'ai-assistant',
										title: `${query}`,
										url: '#',
										queryID: 'ai-assistant',
										position: 0,
									} as ContentHit,
								];
							},
							getItemUrl() {
								return '#';
							},
						});
					}

					// Add regular Algolia source
					sources.push({
						sourceId: ALGOLIA_FACET_KEY,
						getItems({ query }: { query: string }) {
							return getAlgoliaResults({
								searchClient: algoliaSearchClient,
								queries: [
									{
										indexName: searchIndex,
										query,
										params: queryParams,
									},
								],
								transformResponse({ results, hits }) {
									// tranform response so that each hit also contains its position and queryID for analytics purposes
									const [result] = results;
									const queryID = (result as SearchResponse)?.queryID;
									const [originalHits] = hits;
									return (originalHits as unknown as Hit<ContentHit>[])?.map(
										(hit, index: number) => ({
											...hit,
											queryID: queryID as string,
											position: index + 1,
										}),
									);
								},
							});
						},
						getItemUrl({ item }: ItemUrlProps) {
							return item.url;
						},
					});

					return sources;
				},
				placeholder,
				openOnFocus: false,
				stallThreshold: 500,
			}),
		[aiEnabled],
	);

	return {
		autocompleteState,
		autocomplete,
	};
};

export default useAlogoliaAutoComplete;
